import { useMemo } from 'react';
import { DesignIntent, DesignIntentConfig, LanguageCode } from '../../types';

type Helper = {
  message: string;
  type: 'error' | 'warning';
  title: string;
};

type Keys = keyof DesignIntent | keyof DesignIntentConfig;

type Message = {
  key: Keys;
  text: string;
  validation: (value: any) => boolean;
  title?: string;
};

type HelpersResponse = {
  helpers: Helper[];
  isValid: boolean;
};

const fields: Message[] = [
  {
    key: 'name',
    text: 'Name',
    validation: (value: string) => value?.length > 2
  },
  {
    key: 'description',
    text: 'Description',
    validation: (value: string) => value?.length > 4
  },
  {
    key: 'localeData',
    text: 'Utterances (minimum 3 in English, ideally 5)',
    validation: (value: any) => value?.[LanguageCode.englishUs]?.phrases?.length >= 3,
    title: `At the moment only the main language, English (${LanguageCode.englishUs}) requires the minimum number of utterances. Other languages are optional.`
  }
];

export function useIntentFormHelpers(
  intent: Partial<DesignIntent>,
  skip: Keys[] = []
): HelpersResponse {
  return useMemo(() => {
    const helpers = fields.filter((field) => {
      return !skip.includes(field.key);
    }).map((field): Helper => {
      const value = (field.key in intent) ? (
        intent?.[field.key as keyof DesignIntent]
      ) : intent?.props?.config?.[field.key as keyof DesignIntentConfig];
      const isValid = field.validation(value);
      return {
        message: field.text,
        type: isValid ? 'warning' : 'error',
        title: field.title || ''
      };
    });

    return {
      helpers,
      isValid: !helpers.some((helper) => helper.type === 'error')
    };
  }, [intent]);
}
